import { z } from "zod";

export const planningId = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9_:-]+$/);
const text = z.string().max(500_000);
const title = z.string().trim().min(1).max(2000);
const date = z.string().datetime();
const lane = z.enum(["intake", "routine", "functional_attention", "engineering_attention", "engineering", "need_decision"]);
export const planningAttachment = z.object({
  id: planningId,
  name: title,
  size: z
    .number()
    .int()
    .min(0)
    .max(50 * 1024 * 1024),
  mimeType: z.string().max(200),
  origin: z.enum(["local", "jira"]),
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  url: z.string().url().optional(),
  issueKey: z.string().max(200).optional(),
  error: z.string().max(1000).optional(),
});
const choice = z.object({
  id: z.string(),
  label: text,
  description: text,
  recommended: z.boolean(),
});
const questionType = z.enum(["functional", "technical"]);
const questionInput = z.object({
  type: questionType.nullable().optional(),
  blocking: z.boolean().optional(),
  title,
  context: text,
  prompt: title,
  choices: z.array(choice).max(20).optional(),
});
const decisionInput = z.object({
  question: title,
  answer: text.min(1),
  type: questionType.nullable().optional(),
  sourceQuestionId: planningId.nullable().optional(),
  askedAt: date.nullable().optional(),
});
const decision = decisionInput.extend({
  id: planningId,
  featureId: planningId,
  position: z.number().int(),
  createdAt: date,
  updatedAt: date,
});
const question = questionInput.extend({
  id: planningId,
  featureId: planningId,
  createdAt: date,
  blocking: z.boolean().optional(),
});
const score = z.object({
  value: z.number().int().min(1).max(5),
  explanation: text,
});
export const planningEvaluation = z.object({
  featureId: planningId,
  inputUpdatedAt: date.optional(),
  repositoryRevision: z.object({
    repository: text,
    branch: z.string().nullable(),
    commitSha: z.string(),
    evaluatedAt: date,
  }),
  work: score,
  // Retained for older clients and persisted reports during the additive rollout.
  attention: score.optional(),
  functionalAttention: score.optional(),
  engineeringAttention: score.optional(),
  risk: score,
  freedom: score.optional(),
  openQuestions: z.array(questionInput.extend({ id: z.string(), type: questionType, blocking: z.boolean() })).max(100).optional(),
  confidence: z.enum(["low", "medium", "high"]),
  confidenceExplanation: text.nullable(),
  surfaces: z.array(text),
  dependencies: z.array(text),
  constraints: z.array(text),
  evidence: z.array(text),
  summary: text,
  unresolvedIntent: z.boolean().optional(),
  recommendedLane: lane.nullable().optional(),
  overrideReason: text.nullable().optional(),
}).refine((value) => value.functionalAttention && value.engineeringAttention
  ? true
  : !value.functionalAttention && !value.engineeringAttention && !!value.attention && !!value.freedom,
  { message: "Provide both functional and engineering attention scores (or a complete legacy evaluation)." });
export const planningSource = z.object({
  key: title,
  title,
  content: text,
  updatedAt: z.string(),
  issueType: z.string(),
  parentKey: z.string().nullable(),
  status: z.string(),
  statusCategory: z.string().optional(),
  labels: z.array(z.string()),
  url: z.string().url(),
  attachments: z.array(planningAttachment).optional(),
});
export const planningFeature = z.object({
  assigneeId: planningId.nullable().optional(),
  triageHistory: z.array(z.object({
    at: date,
    lane,
    functionalAttention: z.number().int().min(1).max(5),
    engineeringAttention: z.number().int().min(1).max(5),
    risk: z.number().int().min(1).max(5),
  })).optional(),
  id: planningId,
  projectId: planningId,
  title,
  content: text,
  status: z.enum(["todo", "done"]),
  position: z.number().int(),
  createdAt: date,
  updatedAt: date,
  decisions: z.array(decision),
  openQuestions: z.array(question),
  attachments: z.array(planningAttachment).optional(),
  archived: z.boolean().optional(),
  jiraSource: planningSource.optional(),
  analysisFailure: z.object({ message: text, at: date }).nullable().optional(),
  triage: z
    .object({
      inputUpdatedAt: date,
      commitSha: z.string(),
      computedLane: lane,
      recommendedLane: lane.nullable(),
      overrideReason: text.nullable(),
      effectiveLane: lane,
    })
    .optional(),
});
export const planningRecord = z.object({
  feature: planningFeature,
  evaluation: planningEvaluation.optional(),
});
export const planningProjectInput = z.object({
  name: title,
  jiraQuery: z.string().max(10000).optional(),
  jiraSite: z.string().max(2048).refine((value) => {
    if (!value) return true;
    try {
      const url = new URL(value);
      return url.protocol === "https:" && url.hostname.endsWith(".atlassian.net") && !url.port && !url.username && !url.password && value === url.origin;
    } catch {
      return false;
    }
  }, "Expected a Jira Cloud site origin").optional(),
  repositoryId: z.number().int().nullable().default(null),
  repositoryStaleness: z.enum(["head", "inputs"]).default("head"),
});
export const planningCommand = z.discriminatedUnion("type", [
  z.object({ type: z.literal("assign"), assigneeId: planningId.nullable() }),
  z.object({ type: z.literal("edit"), title, content: text }),
  z.object({
    type: z.literal("decision-create"),
    id: planningId,
    input: decisionInput,
  }),
  z.object({
    type: z.literal("decision-update"),
    id: planningId,
    input: decisionInput,
  }),
  z.object({ type: z.literal("decision-delete"), id: planningId }),
  z.object({
    type: z.literal("question-record"),
    id: planningId,
    input: questionInput,
  }),
  z.object({
    type: z.literal("question-resolve"),
    id: planningId,
    decisionId: planningId,
    answer: text.min(1),
  }),
  z.object({ type: z.literal("invalidate") }),
  z.object({ type: z.literal("discard-analysis") }),
  z.object({ type: z.literal("failure"), message: text }),
  z.object({
    type: z.literal("blocked"),
    expectedUpdatedAt: date,
    commitSha: z.string(),
  }),
  z.object({
    type: z.literal("complete"),
    expectedUpdatedAt: date,
    evaluation: planningEvaluation,
    decisions: z.array(decisionInput),
  }),
  z.object({ type: z.literal("jira"), source: planningSource }),
  z.object({
    type: z.literal("attachment-add"),
    attachments: z.array(planningAttachment).max(20),
  }),
  z.object({ type: z.literal("attachment-remove"), id: planningId }),
]);
export const planningMutation = z.object({
  commandId: planningId,
  leaseId: planningId.optional(),
  command: planningCommand,
});
export type PlanningRecord = z.infer<typeof planningRecord>;
export type PlanningCommand = z.infer<typeof planningCommand>;
